export const ONBOARDING_SCHOOL_SYSTEM_DETAILS_KEY = 'onboarding_school_system_details'

export interface StoredPrefilledClassEntry {
  name: string
  subjects: string[]
}

export interface StoredLevelDetail {
  classes: StoredPrefilledClassEntry[]
}

export type StoredSchoolSystemDetails = Record<string, Record<string, StoredLevelDetail>>

const uniqueStrings = (values: string[]) =>
  Array.from(new Set(values.map((value) => value.trim()).filter((value) => value.length > 0)))

const splitSubjects = (raw: string) => {
  const value = raw.trim()
  if (!value) return []

  const output: string[] = []
  let token = ''
  let depth = 0

  for (const ch of value) {
    if (ch === '(') depth += 1
    if (ch === ')' && depth > 0) depth -= 1

    if (ch === ',' && depth === 0) {
      const clean = token.trim()
      if (clean) output.push(clean)
      token = ''
      continue
    }

    token += ch
  }

  const tail = token.trim()
  if (tail) output.push(tail)

  return output
}

const normalizeSubjectNames = (raw: unknown): string[] => {
  if (Array.isArray(raw)) {
    return uniqueStrings(
      raw.flatMap((item) => {
        if (typeof item === 'string') return splitSubjects(item)
        if (item && typeof item === 'object') {
          const name = String(
            (item as Record<string, unknown>).name ??
              (item as Record<string, unknown>).subject ??
              (item as Record<string, unknown>).subjectName ??
              ''
          ).trim()
          return name ? [name] : []
        }
        return []
      })
    )
  }

  if (typeof raw === 'string') return uniqueStrings(splitSubjects(raw))

  if (raw && typeof raw === 'object') {
    const name = String(
      (raw as Record<string, unknown>).name ??
        (raw as Record<string, unknown>).subject ??
        (raw as Record<string, unknown>).subjectName ??
        ''
    ).trim()
    return name ? [name] : []
  }

  return []
}

const normalizeClassEntry = (raw: unknown): StoredPrefilledClassEntry | null => {
  if (typeof raw === 'string') {
    const name = raw.trim()
    return name ? {name, subjects: []} : null
  }

  if (!raw || typeof raw !== 'object') return null

  const record = raw as Record<string, unknown>
  const name = String(
    record.name ?? record.class ?? record.className ?? record.grade ?? record.gradeName ?? ''
  ).trim()

  if (!name) return null

  const subjects = normalizeSubjectNames(
    record.subjects ?? record.subject ?? record.subjectNames ?? record.subject_names ?? []
  )

  return {name, subjects}
}

const getLevelClasses = (level: Record<string, unknown>) => {
  const rawClasses =
    level.classes ?? level.classNames ?? level.class_names ?? level.classList ?? level.class_list

  if (!Array.isArray(rawClasses)) return []

  return uniqueStrings(
    rawClasses
      .map((item) => normalizeClassEntry(item))
      .filter((item): item is StoredPrefilledClassEntry => item !== null)
      .map((item) => JSON.stringify(item))
  ).map((item) => JSON.parse(item) as StoredPrefilledClassEntry)
}

export const normalizeSchoolSystemDetailsResponse = (
  systemsData: unknown[]
): StoredSchoolSystemDetails => {
  const output: StoredSchoolSystemDetails = {}

  systemsData.forEach((system) => {
    if (!system || typeof system !== 'object') return

    const systemRecord = system as Record<string, unknown>
    const curriculumName = String(systemRecord.system ?? systemRecord.name ?? '').trim()
    const levels = Array.isArray(systemRecord.levels) ? systemRecord.levels : []

    if (!curriculumName || levels.length === 0) return

    const levelDetails: Record<string, StoredLevelDetail> = {}

    levels.forEach((level) => {
      if (!level || typeof level !== 'object') return

      const levelRecord = level as Record<string, unknown>
      const levelName = String(levelRecord.level ?? levelRecord.name ?? '').trim()
      if (!levelName) return

      levelDetails[levelName] = {
        classes: getLevelClasses(levelRecord),
      }
    })

    output[curriculumName] = levelDetails
  })

  return output
}

export const readStoredSchoolSystemDetails = (): StoredSchoolSystemDetails => {
  try {
    const raw = localStorage.getItem(ONBOARDING_SCHOOL_SYSTEM_DETAILS_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

export const writeStoredSchoolSystemDetails = (details: StoredSchoolSystemDetails) => {
  localStorage.setItem(ONBOARDING_SCHOOL_SYSTEM_DETAILS_KEY, JSON.stringify(details))
}

export const clearStoredSchoolSystemDetails = () => {
  localStorage.removeItem(ONBOARDING_SCHOOL_SYSTEM_DETAILS_KEY)
}
